import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { NAV } from '../app/nav';
import { navItemLabel } from '../app/navAr';
import { visibleNav } from '../app/permissions';
import { useAuth } from '../app/auth';
import { useUi } from '../app/ui';
import { NavIcon } from './NavIcon';

export interface Command {
  route: string;
  label: string;
  group: string;
  groupKey: string;
}

/** Every screen this user may open, as one flat list — the palette's whole world. */
export function commands(groups: ReturnType<typeof visibleNav>, lang: string): Command[] {
  return groups.flatMap((g) =>
    g.items.map((i) => ({
      route: i.route,
      label: navItemLabel(i.route, i.label, lang),
      group: lang === 'ar' ? g.labelAr : g.label,
      groupKey: g.key,
    })),
  );
}

/**
 * Ranks a screen against what has been typed.
 *
 * Subsequence rather than substring, so "sto" finds "Stock on hand" and "rvcl" finds "Receiving vouchers · close".
 * A word that starts with the term outranks one that merely contains it, and the route counts too, so somebody who
 * knows the system can type `wf/inb` and go straight there. Returns null when it does not match at all.
 */
export function score(command: Command, term: string): number | null {
  const q = term.trim().toLowerCase();
  if (q.length === 0) return 0;
  const label = command.label.toLowerCase();
  const route = command.route.toLowerCase();
  const group = command.group.toLowerCase();

  if (label.startsWith(q)) return 100;
  if (label.split(/[\s(·/-]+/).some((w) => w.startsWith(q))) return 90;
  if (label.includes(q)) return 80;
  if (route.includes(q)) return 70;
  if (group.startsWith(q)) return 60;
  // a subsequence of the label: "sohnd" still finds "Stock on hand"
  let at = 0;
  for (const c of q) {
    at = label.indexOf(c, at) + 1;
    if (at === 0) return null;
  }
  return 40;
}

export const rank = (all: readonly Command[], term: string): Command[] =>
  all
    .map((c) => ({ c, s: score(c, term) }))
    .filter((x): x is { c: Command; s: number } => x.s !== null)
    .sort((a, b) => b.s - a.s || a.c.label.localeCompare(b.c.label))
    .slice(0, 40)
    .map((x) => x.c);

/**
 * Ctrl+K — go anywhere by typing.
 *
 * The system has a hundred and three screens behind eleven collapsed nav groups, which is a lot of opening and
 * scanning for somebody who already knows they want the shelf-life monitor. This is the shortcut past that: type a
 * few letters of a screen, press Enter, and you are on it. It shows only screens the signed-in user may open, so it
 * never offers a door that answers 403.
 */
export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lang = useUi((s) => s.lang);
  const open = useUi((s) => s.paletteOpen);
  const setOpen = useUi((s) => s.setPaletteOpen);
  const permissions = useAuth((s) => s.user?.permissions);
  const signedIn = useAuth((s) => s.user !== null);
  const [term, setTerm] = useState('');
  const [cursor, setCursor] = useState(0);
  const box = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);

  const all = useMemo(
    () => commands(visibleNav(NAV, (code) => permissions?.includes(code) ?? false), lang),
    [permissions, lang],
  );
  const hits = useMemo(() => rank(all, term), [all, term]);

  // Ctrl+K anywhere, including from inside a field — it is the one shortcut that has to work wherever you are
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [setOpen]);

  const go = (route: string) => {
    setOpen(false);
    setTerm('');
    setCursor(0);
    void navigate(route);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = e.key === 'ArrowDown' ? cursor + 1 : cursor - 1;
      const at = Math.max(0, Math.min(hits.length - 1, next));
      setCursor(at);
      list.current?.children[at]?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (e.key === 'Enter' && hits[cursor]) {
      e.preventDefault();
      go(hits[cursor].route);
    }
  };

  if (!signedIn) return null;
  return (
    <Modal
      open={open}
      onCancel={() => { setOpen(false); }}
      footer={null}
      closable={false}
      width={560}
      destroyOnHidden
      afterOpenChange={(o) => { if (o) box.current?.focus(); }}
      className="palette"
      styles={{ body: { padding: 0 } }}
    >
      <div className="palette-box">
        <input
          ref={box}
          className="palette-input"
          type="search"
          aria-label={t('palette.label')}
          placeholder={t('palette.placeholder')}
          value={term}
          onChange={(e) => { setTerm(e.target.value); setCursor(0); }}
          onKeyDown={onKeyDown}
        />
        <ul className="palette-list" ref={list} role="listbox" aria-label={t('palette.label')}>
          {hits.length === 0 && <li className="palette-empty">{t('palette.none', { term })}</li>}
          {hits.map((c, i) => (
            <li key={c.route}>
              <button
                type="button"
                role="option"
                aria-selected={i === cursor}
                className={i === cursor ? 'on' : undefined}
                onMouseEnter={() => { setCursor(i); }}
                onClick={() => { go(c.route); }}
              >
                <span className="ico" aria-hidden="true">
                  <NavIcon group={c.groupKey} />
                </span>
                <span className="txt">
                  {c.label}
                  <small>{c.group}</small>
                </span>
                <code>{c.route}</code>
              </button>
            </li>
          ))}
        </ul>
        <div className="palette-foot">{t('palette.hint')}</div>
      </div>
    </Modal>
  );
}
